import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomer } from '../services/api';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './CustomerDetailsPage.css';

// Citame appointment statuses → CSS modifier classes (colors from theme vars).
const STATUS_CLASS = {
  pending: 'appt-status--pending',
  confirmed: 'appt-status--confirmed',
  cancelled: 'appt-status--cancelled',
  completed: 'appt-status--completed',
  no_show: 'appt-status--no-show',
};

function CustomerDetailsPage() {
  const { customerId } = useParams();
  const { t, i18n } = useTranslation();
  const { setDynamicLabel } = useBreadcrumb();

  const [customer, setCustomer] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getCustomer(customerId);
        if (!active) return;
        setCustomer(data?.customer || null);
        setAppointments(data?.appointments || []);
      } catch (err) {
        console.error('Error loading customer:', err);
        if (active) setError(t('customers.loadError'));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [customerId, t]);

  useEffect(() => {
    if (customer) setDynamicLabel(customer.name || customer.phone || customerId);
  }, [customer, customerId, setDynamicLabel]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return value;
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!customer) return <div className="empty-state">{t('customers.notFound')}</div>;

  return (
    <div className="customer-details-page">
      <div className="page-header">
        <h1>{customer.name || t('customers.unnamed')}</h1>
      </div>

      <div className="customer-info-card card">
        <div className="info-row">
          <div className="info-item">
            <label>{t('customers.phone')}</label>
            <span>{customer.phone || customer.customerId || '-'}</span>
          </div>
          <div className="info-item">
            <label>{t('customers.appointmentsCount')}</label>
            <span>
              {customer.appointmentsCount != null
                ? customer.appointmentsCount
                : appointments.length}
            </span>
          </div>
          <div className="info-item">
            <label>{t('customers.lastAppointment')}</label>
            <span>{formatDateTime(customer.lastAppointmentAt)}</span>
          </div>
        </div>
      </div>

      <div className="appointments-section card">
        <h3>{t('customers.history.title')}</h3>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('customers.history.when')}</th>
                <th>{t('customers.history.service')}</th>
                <th>{t('customers.history.status')}</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((appt) => (
                <tr key={appt.appointmentId}>
                  <td>{formatDateTime(appt.startTime)}</td>
                  <td>{appt.serviceName || '-'}</td>
                  <td>
                    <span className={`appt-status ${STATUS_CLASS[appt.status] || ''}`}>
                      {t(`agenda.status.${appt.status}`, appt.status || '-')}
                    </span>
                  </td>
                </tr>
              ))}
              {appointments.length === 0 && (
                <tr>
                  <td colSpan="3" className="text-center">
                    {t('customers.history.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetailsPage;
